import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';

import Icon from '../../components/AppIcon';
import payrollService from '../../services/payrollService';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import PayrollDashboardCard from './components/PayrollDashboardCard';
import CalculationEnginePanel from './components/CalculationEnginePanel';
import ComplianceValidationPanel from './components/ComplianceValidationPanel';
import PayslipGeneratorPanel from './components/PayslipGeneratorPanel';
import BankTransferPanel from './components/BankTransferPanel';

const AdvancedPayrollProcessingEngine = () => {
  const [activeTab, setActiveTab] = useState('calculation');
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [minimumWage, setMinimumWage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadPayrollData();
  }, [refreshTrigger]);

  const loadPayrollData = async () => {
    try {
      setLoading(true);
      const [runs, minWage] = await Promise.all([
        payrollService?.getAllRuns(),
        payrollService?.getCurrentMinimumWage()
      ]);
      setPayrollRuns(runs || []);
      setMinimumWage(minWage);
    } catch (err) {
      setError(err?.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters) => {
    // Filter logic implementation
  };

  const handlePayrollProcessed = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Calculate dashboard metrics
  const processingStatus = payrollRuns?.filter(r => r?.payrollStatus === 'processing')?.length || 0;
  const employeeCounts = {
    executive: 12,
    management: 45,
    general: 173
  };
  const totalEmployees = employeeCounts?.executive + employeeCounts?.management + employeeCounts?.general;
  const totalPayrollCost = payrollRuns?.[0]?.totalGross || 0;
  const complianceVerified = payrollRuns?.filter(r => r?.payrollStatus === 'approved')?.length || 0;

  const tabs = [
    { id: 'calculation', label: 'Calculation Engine', icon: 'Calculator' },
    { id: 'compliance', label: 'Compliance Validation', icon: 'Shield' },
    { id: 'payslips', label: 'Payslip Generator', icon: 'FileText' },
    { id: 'transfers', label: 'Bank Transfers', icon: 'CreditCard' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-100">
      <Header />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <GlobalFilterBar onFilterChange={handleFilterChange} />

      <main className="pt-48 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg">
                <Icon name="DollarSign" className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Advanced Payroll Processing Engine</h1>
                <p className="text-gray-600 mt-1">Automated calculations with PNG Employment Act compliance guardrails</p>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            {/* Dashboard Cards - 4 columns */}
            <div className="lg:col-span-4 space-y-4">
              <PayrollDashboardCard
                title="Processing Status"
                value={processingStatus}
                subtitle="Active payroll runs"
                icon="Activity"
                color="blue"
              />
              <PayrollDashboardCard
                title="Employee Count"
                value={totalEmployees}
                subtitle={`Exec: ${employeeCounts?.executive} | Mgmt: ${employeeCounts?.management} | Staff: ${employeeCounts?.general}`}
                icon="Users"
                color="purple"
              />
              <PayrollDashboardCard
                title="Total Payroll Cost"
                value={`PGK ${totalPayrollCost?.toLocaleString()}`}
                subtitle="Current period gross"
                icon="DollarSign"
                color="green"
              />
              <PayrollDashboardCard
                title="Compliance Verified"
                value={complianceVerified}
                subtitle="Approved payroll runs"
                icon="CheckCircle"
                color="emerald"
              />
            </div>

            {/* Main Processing Panel - 8 columns */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex overflow-x-auto">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <Icon name={tab?.icon} className="h-5 w-5" />
                        {tab?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                    </div>
                  ) : error ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800">
                      <Icon name="AlertCircle" className="h-5 w-5 inline mr-2" />
                      {error}
                    </div>
                  ) : (
                    <>
                      {activeTab === 'calculation' && (
                        <CalculationEnginePanel
                          minimumWage={minimumWage}
                          onPayrollProcessed={handlePayrollProcessed}
                        />
                      )}
                      {activeTab === 'compliance' && (
                        <ComplianceValidationPanel
                          minimumWage={minimumWage}
                          payrollRuns={payrollRuns}
                        />
                      )}
                      {activeTab === 'payslips' && (
                        <PayslipGeneratorPanel payrollRuns={payrollRuns} />
                      )}
                      {activeTab === 'transfers' && (
                        <BankTransferPanel payrollRuns={payrollRuns} />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdvancedPayrollProcessingEngine;